import { Column } from '@tanstack/react-table';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CloseIcon from '../../assets/icons/close.svg?react';
import SearchIcon from '../../assets/icons/search.svg?react';
import { useDebounce } from '../../hooks/useDebounce';
import {
  InlineSearchableList,
  SearchableSelectOption,
} from '../SearchableSelect';
import { TableColumnFilter, useFilterPopover } from './TableColumnFilter';

/**
 * Column Filter Components
 *
 * Reusable React components for creating standardized column filters.
 * These work with the existing filter render pattern (e.g., useMachineColumnFilters).
 * Eliminates duplication between different table filter implementations.
 */

// ============================================================================
// ICON COMPONENTS
// ============================================================================

const SortAscendingIcon = memo(() => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    height='16'
    viewBox='0 -960 960 960'
    width='16'
  >
    <path d='M440-160v-487L216-423l-56-57 320-320 320 320-56 57-224-224v487h-80Z' />
  </svg>
));
SortAscendingIcon.displayName = 'SortAscendingIcon';

const SortDescendingIcon = memo(() => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    height='16'
    viewBox='0 -960 960 960'
    width='16'
  >
    <path d='M440-800v487L216-537l-56 57 320 320 320-320-56-57-224 224v-487h-80Z' />
  </svg>
));
SortDescendingIcon.displayName = 'SortDescendingIcon';

// ============================================================================
// SORT BUTTONS COMPONENT
// ============================================================================

interface SortButtonProps {
  direction: 'asc' | 'desc';
  isActive: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const SortButton = memo(function SortButton({
  direction,
  isActive,
  onClick,
}: SortButtonProps) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`flex w-full cursor-pointer items-center gap-3 px-2 py-3
        transition-colors ${
          isActive
            ? 'text-focus'
            : 'hover:bg-surface-focus-support text-secondary'
        }`}
    >
      <span
        className='flex h-5 w-5 shrink-0 items-center justify-center
          [&_path]:fill-current [&_svg]:fill-current'
      >
        {direction === 'asc' ? <SortAscendingIcon /> : <SortDescendingIcon />}
      </span>
      <span className='size-p2'>
        Sort {direction === 'asc' ? 'ascending' : 'descending'}
      </span>
    </button>
  );
});
SortButton.displayName = 'SortButton';

export interface SortButtonsProps<TData = unknown> {
  /** TanStack Table column - required for sorting */
  column: Column<TData, unknown>;
  /** Callback to close the popover after sorting - only needed if used outside FilterPopoverContext */
  onClose?: () => void;
}

/**
 * Sort buttons component for table columns.
 * Integrates with FilterPopoverContext and automatically closes the popover when clicked.
 *
 * Usage:
 *   <SortButtons column={column} />
 */
export const SortButtons = memo(function SortButtons<TData = unknown>({
  column,
  onClose,
}: SortButtonsProps<TData>) {
  const filterPopover = useFilterPopover();
  const sortDirection = column.getIsSorted();

  const handleSort = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, descending: boolean) => {
      e.preventDefault();
      e.stopPropagation();
      column.toggleSorting(descending, false);
      // Close popover - prefer context, fallback to prop
      (filterPopover?.onClose ?? onClose)?.();
    },
    [column, filterPopover, onClose]
  );

  const handleSortAscending = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => handleSort(e, false),
    [handleSort]
  );

  const handleSortDescending = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => handleSort(e, true),
    [handleSort]
  );

  return (
    <>
      <SortButton
        direction='asc'
        isActive={sortDirection === 'asc'}
        onClick={handleSortAscending}
      />
      <SortButton
        direction='desc'
        isActive={sortDirection === 'desc'}
        onClick={handleSortDescending}
      />
    </>
  );
}) as <TData = unknown>(props: SortButtonsProps<TData>) => React.JSX.Element;

// ============================================================================
// CLEAR FILTER BUTTON COMPONENT
// ============================================================================

export interface ClearFilterButtonProps {
  disabled: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const ClearFilterButton = memo(function ClearFilterButton({
  disabled,
  onClick,
}: ClearFilterButtonProps) {
  return (
    <button
      type='button'
      disabled={disabled}
      onClick={onClick}
      className={`text-secondary border-border-secondary flex w-full
        items-center border-t px-2 py-3 transition-colors ${
          !disabled
            ? 'hover:bg-surface-focus-support cursor-pointer'
            : 'cursor-not-allowed opacity-40'
        }`}
    >
      <span className='size-p2 pl-8'>Clear filter</span>
    </button>
  );
});
ClearFilterButton.displayName = 'ClearFilterButton';

// ============================================================================
// SHARED SEARCH ROW
// ============================================================================

export interface FilterSearchRowProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputRef?: React.Ref<HTMLInputElement>;
}

/**
 * The search row every column filter popover opens with - one look for both
 * the text filters and the multi-select filters.
 */
export const FilterSearchRow = memo(function FilterSearchRow({
  value,
  onChange,
  placeholder = 'Search...',
  inputRef,
}: FilterSearchRowProps) {
  return (
    <div className='border-border-secondary border-b'>
      <div className='flex items-center gap-2 p-3'>
        <span
          className='text-primary flex h-4 w-4 shrink-0 items-center
            justify-center [&_path]:fill-current [&_svg]:fill-current'
        >
          <SearchIcon />
        </span>
        <input
          ref={inputRef}
          type='text'
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className='size-p2 text-primary placeholder:text-disabled flex-1
            bg-transparent outline-none'
          onClick={(e) => e.stopPropagation()}
        />
        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            onChange('');
          }}
          className={`text-tertiary hover:text-primary flex h-4 w-4 shrink-0
            cursor-pointer items-center justify-center transition-colors
            [&_path]:fill-current [&_svg]:fill-current
            ${!value ? 'invisible' : ''}`}
          aria-label='Clear search'
        >
          <CloseIcon className='h-3 w-3' />
        </button>
      </div>
    </div>
  );
});

// ============================================================================
// SEARCH FILTER (Text Input)
// ============================================================================

export interface SearchFilterProps<TData = unknown> {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  column?: Column<TData, unknown>;
  onClearFilter?: () => void;
}

/**
 * Search input filter component with debouncing
 * Used for text-based filtering (e.g., display name, alarm name)
 */
/**
 * Internal component that manages the search input state
 * Separated to allow key-based remounting when external value clears
 */
const SearchFilterInput = memo(function SearchFilterInput({
  initialValue,
  onChange,
  placeholder,
}: {
  initialValue: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [localValue, setLocalValue] = useState(initialValue);
  const debouncedValue = useDebounce(localValue, 600);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update parent when debounced value changes
  useEffect(() => {
    onChange(debouncedValue);
  }, [debouncedValue, onChange]);

  useEffect(() => {
    // Focus the search input when the popover opens
    inputRef.current?.focus();
  }, []);

  return (
    <FilterSearchRow
      value={localValue}
      onChange={setLocalValue}
      placeholder={placeholder}
      inputRef={inputRef}
    />
  );
});
SearchFilterInput.displayName = 'SearchFilterInput';

// Inner content component that can access the filter popover context
interface SearchFilterContentProps<TData = unknown> {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  column?: Column<TData, unknown>;
  onClearFilter?: () => void;
}

const SearchFilterContent = memo(function SearchFilterContent<TData = unknown>({
  value,
  onChange,
  placeholder,
  column,
  onClearFilter,
}: SearchFilterContentProps<TData>) {
  const filterPopover = useFilterPopover();
  const hasValue = useMemo(() => value.length > 0, [value]);
  const canSort = useMemo(() => column?.getCanSort() ?? false, [column]);
  const sortDirection = column?.getIsSorted();
  const isSorted = useMemo(() => !!sortDirection, [sortDirection]);
  const hasFilterOrSort = useMemo(
    () => hasValue || isSorted,
    [hasValue, isSorted]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (hasFilterOrSort) {
        onClearFilter?.();
        column?.clearSorting();
        filterPopover?.onClose();
      }
    },
    [hasFilterOrSort, onClearFilter, column, filterPopover]
  );

  return (
    <div>
      <SearchFilterInput
        key={value}
        initialValue={value}
        onChange={onChange}
        placeholder={placeholder}
      />

      {/* Sort and Clear Actions */}
      {(canSort || onClearFilter) && (
        <div>
          {canSort && column && <SortButtons column={column} />}

          {onClearFilter && (
            <ClearFilterButton
              disabled={!hasFilterOrSort}
              onClick={handleClear}
            />
          )}
        </div>
      )}
    </div>
  );
}) as <TData = unknown>(
  props: SearchFilterContentProps<TData>
) => React.JSX.Element;

export const SearchFilter = memo(function SearchFilter<TData = unknown>({
  value,
  onChange,
  placeholder = 'Search...',
  column,
  onClearFilter,
}: SearchFilterProps<TData>) {
  const hasValue = useMemo(() => value.length > 0, [value]);

  return (
    <TableColumnFilter isActive={hasValue} ariaLabel={placeholder}>
      <SearchFilterContent
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        column={column}
        onClearFilter={onClearFilter}
      />
    </TableColumnFilter>
  );
}) as <TData = unknown>(props: SearchFilterProps<TData>) => React.JSX.Element;

// ============================================================================
// SELECT FILTER (Multi-select List)
// ============================================================================

export interface SelectFilterProps<TData = unknown> {
  options: SearchableSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  loading?: boolean;
  column?: Column<TData, unknown>;
  onClearFilter?: () => void;
}

// Inner content component that can access the filter popover context
interface SelectFilterContentProps<TData = unknown> {
  options: SearchableSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  loading: boolean;
  column?: Column<TData, unknown>;
  onClearFilter?: () => void;
}

const SelectFilterContent = memo(function SelectFilterContent<TData = unknown>({
  options,
  selectedValues,
  onChange,
  placeholder,
  loading,
  column,
  onClearFilter,
}: SelectFilterContentProps<TData>) {
  const filterPopover = useFilterPopover();
  const hasValue = useMemo(
    () => selectedValues.length > 0,
    [selectedValues.length]
  );
  const canSort = useMemo(() => column?.getCanSort() ?? false, [column]);
  const sortDirection = column?.getIsSorted();
  const isSorted = useMemo(() => !!sortDirection, [sortDirection]);
  const hasFilterOrSort = useMemo(
    () => hasValue || isSorted,
    [hasValue, isSorted]
  );

  const displayPlaceholder = useMemo(
    () => (loading ? 'Loading...' : placeholder),
    [loading, placeholder]
  );

  const handleClearFilter = useCallback(() => {
    if (onClearFilter) {
      onClearFilter();
    } else {
      onChange([]);
    }
    column?.clearSorting();
  }, [onClearFilter, onChange, column]);

  return (
    <InlineSearchableList
      options={options}
      selectedValues={selectedValues}
      onSelectionChange={onChange}
      placeholder={displayPlaceholder}
      column={canSort ? column : undefined}
      onClearFilter={handleClearFilter}
      hasFilterOrSort={hasFilterOrSort}
      onClose={filterPopover?.onClose}
    />
  );
}) as <TData = unknown>(
  props: SelectFilterContentProps<TData>
) => React.JSX.Element;

/**
 * Multi-select filter component
 * Used for selecting from a list of options (e.g., customers, plants, statuses)
 */
export const SelectFilter = memo(function SelectFilter<TData = unknown>({
  options,
  selectedValues,
  onChange,
  placeholder = 'Search...',
  loading = false,
  column,
  onClearFilter,
}: SelectFilterProps<TData>) {
  const hasValue = useMemo(
    () => selectedValues.length > 0,
    [selectedValues.length]
  );

  return (
    <TableColumnFilter isActive={hasValue} ariaLabel={placeholder}>
      <SelectFilterContent
        options={options}
        selectedValues={selectedValues}
        onChange={onChange}
        placeholder={placeholder}
        loading={loading}
        column={column}
        onClearFilter={onClearFilter}
      />
    </TableColumnFilter>
  );
}) as <TData = unknown>(props: SelectFilterProps<TData>) => React.JSX.Element;
